// src/pages/TicketRecall/modals/RecallDetailModal.tsx
// Modal xem chi tiết 1 phiếu thu hồi vé ế — bản rút gọn của DeliveryDetailModal.tsx, bỏ In phiếu.
// Có dòng "Nguồn" (Kho tổng / tên đại lý) vì phiếu thu hồi có thể rút từ kho tổng hoặc từ 1 đại lý.

import React, { useEffect, useState } from 'react';
import { Button, Collapse, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import moment from 'moment';
import { AgentMap, getAgentName } from 'helpers/agentLookup';
import { TICKET_RECALL_STATUS_COLORS, TICKET_RECALL_STATUS_LABELS, TicketRecall } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

interface RecallDetailModalProps {
  isOpen: boolean;
  recall: TicketRecall | null;
  agentMap: AgentMap;
  onClose: () => void;
}

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '-';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const COLLAPSE_THRESHOLD = 5;

const RecallDetailModal: React.FC<RecallDetailModalProps> = ({ isOpen, recall, agentMap, onClose }) => {
  const lineCount = recall?.lines?.length || 0;
  const [linesOpen, setLinesOpen] = useState(true);

  useEffect(() => {
    if (isOpen) setLinesOpen(lineCount <= COLLAPSE_THRESHOLD);
  }, [isOpen, lineCount]);

  if (!recall) return null;

  const color = recall.statusBadgeColor || TICKET_RECALL_STATUS_COLORS[String(recall.status)] || 'secondary';
  const label = recall.statusName || TICKET_RECALL_STATUS_LABELS[String(recall.status)] || st('khong_ro_text');

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{st('chi_tiet_phieu_thu_hoi_ve_title')} {recall.recallCode}</ModalHeader>
      <ModalBody>
        <div className="table-responsive">
          <table className="table table-bordered table-sm mb-0">
            <tbody>
              <tr>
                <td width="30%" className="fw-semibold bg-light">{st('ma_phieu_column')}</td>
                <td>{recall.recallCode}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('trang_thai_column')}</td>
                <td>
                  <span className={`badge bg-${color}-subtle text-${color} text-uppercase`}>{label}</span>
                </td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ke_hoach_phat_hanh_column')}</td>
                <td>{recall.planCode || recall.issuancePlanId}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('nguon_thu_hoi_label')}</td>
                <td>{recall.agentId ? getAgentName(agentMap, recall.agentId) : st('kho_tong_text')}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('tong_so_ve_text')}</td>
                <td className="fw-semibold">{formatNumber(recall.totalQuantity)}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('nguoi_lap_column')}</td>
                <td>{recall.createdByName || '-'}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ngay_xac_nhan_text')}</td>
                <td>{recall.confirmedAt ? moment(recall.confirmedAt).format('DD/MM/YYYY HH:mm') : '-'}</td>
              </tr>
              <tr>
                <td className="fw-semibold bg-light">{st('ghi_chu_label')}</td>
                <td>{recall.note || '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
          <h6 className="mb-0">{st('chi_tiet_dai_so_title')}{formatNumber(lineCount)} {st('dong_title')}</h6>
          <Button
            color="link"
            size="sm"
            className="p-0 text-decoration-none"
            type="button"
            aria-expanded={linesOpen}
            onClick={() => setLinesOpen((prev) => !prev)}
          >
            {linesOpen ? st('thu_gon_text') : st('mo_rong_text')}
            <i className={`${linesOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} align-bottom ms-1`}></i>
          </Button>
        </div>

        <Collapse isOpen={linesOpen}>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>{st('ky_hieu_column')}</th>
                  <th>{st('tu_so_column')}</th>
                  <th>{st('den_so_column')}</th>
                  <th className="text-end">{st('so_luong_column')}</th>
                </tr>
              </thead>
              <tbody>
                {lineCount === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      {st('khong_co_dong_dai_so_text')}
                    </td>
                  </tr>
                ) : (
                  (recall.lines || []).map((line, index) => (
                    <tr key={line.id ?? index}>
                      <td>{line.symbol}</td>
                      <td>{line.rangeStart}</td>
                      <td>{line.rangeEnd}</td>
                      <td className="text-end">{formatNumber(line.quantity)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Collapse>

        {recall.agentId && (
          <>
            {/* Mục 11.b, README_UPDATE.md — chi tiết các phiếu giao gốc mà phiếu thu hồi này "ăn"
                theo, phục vụ đối chiếu giao → thu hồi. */}
            <h6 className="mt-4 mb-2">
              {st('phieu_giao_goc_title')}{formatNumber(recall.sourceDeliveries?.length || 0)} {st('phieu_title')}
            </h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{st('ma_phieu_giao_column')}</th>
                    <th>{st('thoi_gian_giao_column')}</th>
                    <th>{st('ky_hieu_column')}</th>
                    <th>{st('dai_so_column')}</th>
                    <th className="text-end">{st('tong_ve_giao_column')}</th>
                  </tr>
                </thead>
                <tbody>
                  {!recall.sourceDeliveries || recall.sourceDeliveries.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-3">
                        {st('khong_xac_dinh_duoc_phieu_giao_text')}
                      </td>
                    </tr>
                  ) : (
                    recall.sourceDeliveries.map((delivery, index) => (
                      <tr key={`${delivery.deliveryCode}-${index}`}>
                        <td className="fw-medium text-primary">{delivery.deliveryCode}</td>
                        <td>
                          {delivery.deliveredAt
                            ? moment(delivery.deliveredAt).format('DD/MM/YYYY HH:mm')
                            : '-'}
                        </td>
                        <td>{delivery.symbol}</td>
                        <td>
                          {delivery.rangeStart} - {delivery.rangeEnd}
                        </td>
                        <td className="text-end">{formatNumber(delivery.quantity)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mục 11.b — so sánh Giao/Thu hồi/Bán và công nợ ước tính của CẢ đại lý trong cùng kế
                hoạch (không chỉ riêng phiếu đang xem). */}
            <h6 className="mt-4 mb-2">{st('doi_chieu_cong_no_dai_ly_title')}</h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <td className="fw-semibold bg-light" width="40%">{st('tong_ve_da_giao_text')}</td>
                    <td>{formatNumber(recall.agentTotalDeliveredQuantity)}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('tong_ve_da_thu_hoi_text')}</td>
                    <td>{formatNumber(recall.agentTotalRecalledQuantity)}</td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('so_ve_da_ban_duoc_text')}</td>
                    <td className="text-success fw-semibold">
                      {formatNumber(recall.agentTotalSoldQuantity)}
                    </td>
                  </tr>
                  <tr>
                    <td className="fw-semibold bg-light">{st('cong_no_uoc_tinh_text')}</td>
                    <td className="fw-semibold">{formatCurrency(recall.agentDebtValue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          {st('dong_button')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RecallDetailModal;
